import { SHELL, Reveal, Mono, Btn } from "@/components/bits";
import { CONFIG } from "@/lib/config";

const SECTIONS = [
    {
        h: "01 — The Fragmentation of Real-World Capital",
        p: [
            "Every economy runs on real assets: buildings that collect rent, loans that collect interest, invoices that collect payments, cargo that moves goods. The activity is global and constant. The systems that record it are not — they are local registries, closed ledgers, PDFs and filing cabinets.",
            "Fragmentation is not a small operational annoyance. It is the reason capital against real assets moves in paper time while everything else moves in network time.",
        ],
    },
    {
        h: "02 — The Rise of Tokenized Assets",
        p: [
            "Tokenization proved that financial instruments can live onchain: stablecoins settled trillions, and tokenized funds like Circle's USYC showed that cash-equivalent products can be represented and administered as tokens.",
            "The next frontier is the asset class beneath the funds — the properties, receivables and trade flows that were never born onchain. Circle documents this direction directly in its work on tokenizing real-world assets with Circle contracts.",
        ],
    },
    {
        h: "03 — From Tokenization to Programmable Capital",
        p: [
            "A token is a representation. Capital is a behavior — allocation, routing, accounting, settlement. The difference between a static token and programmable capital is the difference between a photograph of a machine and the machine itself.",
            "VERA's thesis is that real-world assets deserve the machine: pools with accounting, vaults with rules, and settlement that happens at network speed.",
        ],
    },
    {
        h: "04 — Why Cash Flow Matters",
        p: [
            "Assets matter because they produce economic activity. Rent, interest, payments — cash flow is the real engine underneath every real-world asset thesis. A capital layer that ignores cash flow is a capital layer that ignores the asset.",
            "This is why VERA's architecture treats cash flow as a first-class object: verified, structured and settled, not just referenced.",
        ],
    },
    {
        h: "05 — Asset Pools",
        p: [
            "Single assets are lumpy and hard to underwrite. Pools create structure: shared accounting, standard reporting and one programmable interface. The RWA category — with protocols like Centrifuge documenting years of pool-based infrastructure — demonstrates exactly this demand.",
        ],
    },
    {
        h: "06 — Vaults",
        p: [
            "Vaults are the interface between capital providers and asset pools. Capital enters in USDC, gets allocated by rules, and exits the same way. Vaults make participation a question of states and constraints — eligibility, limits, queues — rather than negotiation.",
        ],
    },
    {
        h: "07 — Verification",
        p: [
            "Verification before liquidity. An asset enters the layer only after existence, ownership, economic substance and documentation are checked. Verification reduces risk; it never eliminates it — which is why the risk disclosure is as much a part of this thesis as the pipeline.",
        ],
    },
    {
        h: "08 — USDC Settlement",
        p: [
            "Settlement is the product. USDC gives every participant the same unit — a fully reserved digital dollar — so obligations can clear without currency ambiguity or volatile gas tokens.",
        ],
    },
    {
        h: "09 — Arc as the Economic Layer",
        p: [
            "Arc is designed by Circle as an open Layer 1 for financial markets, real-time money movement, asset issuance and agentic economic activity — with fees paid in USDC, deterministic instant settlement, and full EVM compatibility. For real-world assets, that is precisely the execution layer the thesis needs.",
        ],
    },
    {
        h: "10 — The VERA Thesis",
        p: [
            "VERA transforms verified real-world assets and their underlying economic activity into programmable onchain capital. Real assets. Programmable capital. USDC settlement on Arc.",
            "Nothing in this thesis requires inventing a statistic. The assets already exist. The settlement layer already exists. What is being built is the layer between them.",
        ],
    },
    {
        h: "11 — The Road Ahead",
        p: [
            "Verification infrastructure, pools, vaults, accounting, settlement — in that order. Structure before scale, and proof before promise. Verify everything on the Proof page, and follow the work on X.",
        ],
    },
];

export default function Article() {
    return (
        <div data-testid="article-page">
            <section className="relative pt-24 md:pt-32" data-testid="article-hero">
                <SHELL>
                    <Reveal>
                        <div className="relative rounded-lg overflow-hidden border border-white/10 mb-12">
                            <img src={CONFIG.ARTICLE_BANNER} alt="VERA — the world's real assets connected across a globe" className="w-full h-52 sm:h-72 md:h-[440px] object-cover" />
                            <div className="absolute inset-0 bg-gradient-to-t from-vera-bg via-transparent to-transparent" aria-hidden="true" />
                        </div>
                    </Reveal>
                    <div className="max-w-3xl mx-auto pb-16 border-b border-white/10">
                        <Reveal y={14}>
                            <div className="flex items-center gap-4 mb-8">
                                <Mono className="text-vera-gold2">VERA — EDITORIAL</Mono>
                                <span className="hairline w-12" aria-hidden="true" />
                                <Mono className="text-vera-mut">RESEARCH</Mono>
                            </div>
                        </Reveal>
                        <Reveal delay={0.06}>
                            <h1 className="text-3xl sm:text-5xl font-black tracking-tight leading-[1.08] mb-6">
                                THE WORLD IS FULL OF CAPITAL. <span className="text-gold-grad">MOST OF IT IS STILL OFFCHAIN.</span>
                            </h1>
                        </Reveal>
                        <Reveal delay={0.12}>
                            <p className="text-vera-mut text-base sm:text-lg leading-relaxed">Why real-world assets need a programmable capital layer.</p>
                        </Reveal>
                    </div>
                </SHELL>
            </section>

            <article className="py-16 md:py-20">
                <SHELL>
                    <div className="max-w-3xl mx-auto space-y-14">
                        {SECTIONS.map((s, i) => (
                            <Reveal key={s.h} delay={0.02}>
                                <section data-testid={`article-section-${i + 1}`}>
                                    <h2 className="text-xl sm:text-2xl font-bold tracking-tight mb-5 text-vera-white">{s.h}</h2>
                                    {s.p.map((para, j) => (
                                        <p key={j} className="text-vera-mut text-[15px] leading-[1.9] mb-4">{para}</p>
                                    ))}
                                    {i === 1 && (
                                        <p className="text-sm leading-relaxed">
                                            <a href="https://www.circle.com/usyc" target="_blank" rel="noopener noreferrer" data-testid="article-link-usyc" className="text-vera-soft hover:text-white underline underline-offset-4 decoration-vera-blue/40">
                                                Circle — USYC
                                            </a>
                                            {" · "}
                                            <a href="https://www.circle.com/blog/tokenizing-real-world-assets-with-circle-contracts" target="_blank" rel="noopener noreferrer" data-testid="article-link-circle-rwa" className="text-vera-soft hover:text-white underline underline-offset-4 decoration-vera-blue/40">
                                                Circle — Tokenizing Real-World Assets
                                            </a>
                                        </p>
                                    )}
                                    {i === 8 && (
                                        <p className="text-sm leading-relaxed">
                                            <a href="https://www.circle.com/pressroom/circle-launches-arc-mainnet-an-economic-operating-system-for-the-internet" target="_blank" rel="noopener noreferrer" data-testid="article-link-arc" className="text-vera-soft hover:text-white underline underline-offset-4 decoration-vera-blue/40">
                                                Circle — Arc Mainnet announcement
                                            </a>
                                        </p>
                                    )}
                                    {i === 4 && (
                                        <p className="text-sm leading-relaxed">
                                            <a href="https://centrifuge.com" target="_blank" rel="noopener noreferrer" data-testid="article-link-centrifuge" className="text-vera-soft hover:text-white underline underline-offset-4 decoration-vera-blue/40">
                                                Centrifuge — RWA infrastructure
                                            </a>
                                        </p>
                                    )}
                                </section>
                            </Reveal>
                        ))}

                        <Reveal>
                            <div className="border border-white/10 bg-vera-bg2/40 p-8 text-center">
                                <p className="font-mono text-[10px] tracking-[0.3em] text-vera-gold2 mb-6">REAL ASSETS. PROGRAMMABLE CAPITAL.</p>
                                <div className="flex flex-wrap justify-center gap-4">
                                    <Btn variant="blue" to="/assets" testid="article-cta-explore">EXPLORE VERA</Btn>
                                    <Btn variant="ghost" to="/proof" testid="article-cta-proof">VERIFY THE SOURCES</Btn>
                                </div>
                            </div>
                        </Reveal>
                    </div>
                </SHELL>
            </article>
        </div>
    );
}
